import React, { useState, useEffect, useRef } from 'react';
import './BobTheButler.css';

const BobTheButler = () => {
  // The widget used to start visible on every mount, so it reappeared on each
  // page and every reload. It now starts collapsed (just the launcher button)
  // and honours whatever the user last chose.
  const [isVisible, setIsVisible] = useState(() => {
    try {
      return localStorage.getItem('bob_widget_open') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userName, setUserName] = useState('');
  const [askingName, setAskingName] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [currentUtterance, setCurrentUtterance] = useState(null);
  
  const conversationRef = useRef(null);
  const recognitionRef = useRef(null);
  const hasAddedInitialMessage = useRef(false);

  // API endpoint - integrate with the Syntax Map backend
  const CHAT_ENDPOINT = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/bob-chat`;

  const [hasInitialized, setHasInitialized] = useState(false);

  // Open/close the widget and remember the choice, so dismissing Bob sticks
  // across navigation and reloads instead of popping back up.
  const setWidgetOpen = (open) => {
    setIsVisible(open);
    try {
      localStorage.setItem('bob_widget_open', open ? 'true' : 'false');
    } catch (e) {
      // storage unavailable (private mode) — widget still works, it just
      // won't remember the preference
    }
  };

  useEffect(() => {
    if (!hasInitialized) {
      initializeVoices();
      loadUserName();
      setupSpeechRecognition();
      mountFirstMessage();
      setHasInitialized(true);
    }
  }, [hasInitialized]);

  const initializeVoices = () => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const voices = speechSynthesis.getVoices();
        if (voices.length === 0) {
          setTimeout(loadVoices, 200);
          return;
        }
        
        const sortedVoices = voices.slice().sort((a, b) => 
          (a.lang || '').localeCompare(b.lang || '') || a.name.localeCompare(b.name)
        );
        setAvailableVoices(sortedVoices);

        // Find preferred voice (William en-AU, or fallback to English)
        const preferredVoice = sortedVoices.find(v => 
          /william/i.test(v.name) && /en-AU/i.test(v.lang)
        ) || sortedVoices.find(v => 
          /william/i.test(v.name)
        ) || sortedVoices.find(v => 
          /en-AU/i.test(v.lang)
        ) || sortedVoices.find(v => 
          /English/i.test(v.name) || /^en-/i.test(v.lang)
        ) || sortedVoices[0];

        setSelectedVoice(preferredVoice);
      };
      
      speechSynthesis.onvoiceschanged = loadVoices;
      loadVoices();
    }
  };

  const setupSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.lang = 'en-AU';

      recognition.onstart = () => {
        setIsListening(true);
        stopSpeech();
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        setInputMessage((finalTranscript || interimTranscript).trim());
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }
  };

  const loadUserName = () => {
    const storedName = localStorage.getItem('bob_user_name');
    if (storedName) {
      setUserName(storedName);
    }
  };

  const saveUserName = (name) => {
    const cleanName = name.trim();
    setUserName(cleanName);
    localStorage.setItem('bob_user_name', cleanName);
  };

  const mountFirstMessage = () => {
    // Prevent multiple initial messages using ref
    if (hasAddedInitialMessage.current) {
      return;
    }
    hasAddedInitialMessage.current = true;
    
    const storedName = localStorage.getItem('bob_user_name');
    if (!storedName) {
      setAskingName(true);
      const intro = "Hi, welcome to Syntax Map! I'm Bob, your English tutor assistant. What's your name?";
      addMessage('ai', intro);
      // Don't auto-speak on first load (browsers block autoplay)
    } else {
      const greeting = `Welcome back, ${storedName}! How may I assist you with your English learning today?`;
      addMessage('ai', greeting);
      // Don't auto-speak on first load (browsers block autoplay)
      showQuickOptions();
    }
  };

  const addMessage = (sender, message) => {
    const newMessage = {
      id: Date.now() + Math.random(),
      sender,
      message,
      timestamp: new Date()
    };
    
    setConversation(prev => [...prev, newMessage]);
    
    // Auto-scroll to bottom
    setTimeout(() => {
      if (conversationRef.current) {
        conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
      }
    }, 100);
  };

  const cleanTextForSpeech = (text) => {
    return text
      .replace(/\*[^*]*\*/g, '')
      .replace(/--[^-]*--/g, '')
      .replace(/[\*\-]/g, '')
      .replace(/[\u{1F300}-\u{1FAFF}\u{1F1E6}-\u{1F1FF}\u2600-\u27BF]/gu, '')
      .replace(/_/g, ' ')
      .replace(/^\d+\.\s*/gm, '')
      .replace(/[•◦▪▫→←↑↓]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const speakText = (text) => {
    if (!text) return;
    
    // Wait for voices to be available
    if (!selectedVoice && availableVoices.length === 0) {
      setTimeout(() => speakText(text), 500);
      return;
    }
    
    if (currentUtterance) {
      speechSynthesis.cancel();
    }
    
    const cleanText = cleanTextForSpeech(text);
    console.log('Speaking text:', cleanText);
    
    const utterance = new SpeechSynthesisUtterance(cleanText);
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    utterance.onstart = () => console.log('Speech started');
    utterance.onend = () => console.log('Speech ended');
    utterance.onerror = (e) => console.error('Speech error:', e);
    
    setCurrentUtterance(utterance);
    speechSynthesis.speak(utterance);
  };

  const stopSpeech = () => {
    if (currentUtterance) {
      speechSynthesis.cancel();
      setCurrentUtterance(null);
    }
  };

  const sendMessage = async (message) => {
    const msg = message.trim();
    if (!msg) return;

    // Handle name collection
    if (askingName) {
      const nameOnly = msg.replace(/[^a-zA-Z \-'.]/g, '').trim().split(/\s+/)[0] || 'friend';
      const capitalizedName = nameOnly.charAt(0).toUpperCase() + nameOnly.slice(1).toLowerCase();
      
      saveUserName(capitalizedName);
      addMessage('user', capitalizedName);
      setAskingName(false);

      const greeting = `Lovely to meet you, ${capitalizedName}! I'm here to help you with English grammar, vocabulary, and conversation practice. What would you like to work on today?`;
      addMessage('ai', greeting);
      // Speech will work after user interaction
      speakText(greeting);
      showQuickOptions();
      return;
    }

    addMessage('user', msg);
    setIsLoading(true);
    
    // Add thinking indicator
    const thinkingId = Date.now() + Math.random();
    setConversation(prev => [...prev, {
      id: thinkingId,
      sender: 'ai',
      message: '💭 *Bob is thinking...*',
      isThinking: true,
      timestamp: new Date()
    }]);

    try {
      // Get conversation history for context
      const history = conversation
        .filter(m => m && m.message && !m.isThinking && !m.message.includes('💭') && !m.isOptions)
        .slice(-6)
        .map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.message
        }));

      console.log('Sending message to Bob:', { msg, CHAT_ENDPOINT });

      const response = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('jstoken') && {
            'Authorization': `Bearer ${localStorage.getItem('jstoken')}`
          })
        },
        body: JSON.stringify({
          message: msg,
          history: history,
          user_name: userName || null
        })
      });

      console.log('Response status:', response.status);

      // Remove thinking indicator
      setConversation(prev => prev.filter(m => m.id !== thinkingId));

      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        const reply = data.reply || "I apologize, but I'm having trouble processing your request. Could you try again?";
        addMessage('ai', reply);
        speakText(reply);
      } else {
        const errorText = await response.text();
        console.error('Response error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error) {
      // Remove thinking indicator
      setConversation(prev => prev.filter(m => m.id !== thinkingId));
      
      console.error('Error sending message:', error);
      const errorMsg = `I apologize, but I'm having trouble connecting right now. Error: ${error.message}. Please try again in a moment.`;
      addMessage('ai', errorMsg);
      speakText(errorMsg);
    }
    
    setIsLoading(false);
  };

  const showQuickOptions = () => {
    const options = [
      "📚 Grammar Help",
      "🎭 Practice Conversation", 
      "📝 Writing Assistance",
      "🔤 Vocabulary Building",
      "❓ Ask a Question"
    ];
    
    const optionsMessage = {
      id: Date.now() + Math.random(),
      sender: 'ai',
      isOptions: true,
      options: options,
      timestamp: new Date()
    };
    
    setConversation(prev => [...prev, optionsMessage]);
  };

  const selectOption = (option) => {
    // Remove options from conversation
    setConversation(prev => prev.filter(m => !m.isOptions));
    
    addMessage('user', option);
    
    const responses = {
      "📚 Grammar Help": "Excellent choice! What grammar topic would you like to work on? I can help with tenses, sentence structure, punctuation, and more.",
      "🎭 Practice Conversation": "Wonderful! Let's have a conversation. You can choose a topic, or I can suggest scenarios like job interviews, ordering at a restaurant, or casual chat.",
      "📝 Writing Assistance": "I'd be happy to help with your writing! You can share a text for feedback, or I can help you write emails, essays, or other documents.",
      "🔤 Vocabulary Building": "Great! I can help you learn new words, understand word meanings, or practice using words in context. What interests you?",
      "❓ Ask a Question": "Perfect! I'm here to answer any English-related questions you have. What would you like to know?"
    };
    
    setTimeout(() => {
      const response = responses[option] || "How can I help you with that?";
      addMessage('ai', response);
      speakText(response);
    }, 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputMessage.trim() && !isLoading) {
      sendMessage(inputMessage);
      setInputMessage('');
    }
  };

  const toggleMicrophone = () => {
    if (!recognitionRef.current) {
      alert("Voice input isn't supported in this browser.");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.lang = selectedVoice?.lang || 'en-AU';
      recognitionRef.current.start();
    }
  };

  const readLastMessage = () => {
    const lastAiMessage = [...conversation].reverse().find(m => m.sender === 'ai' && !m.isThinking && !m.isOptions);
    if (lastAiMessage) {
      console.log('Reading last message:', lastAiMessage.message);
      speakText(lastAiMessage.message);
    } else {
      console.log('No message to read');
    }
  };

  const testSpeech = () => {
    speakText("Hello! This is a test of my voice. Can you hear me clearly?");
  };

  if (!isVisible) {
    return (
      <button
        className="bob-show-button"
        onClick={() => setWidgetOpen(true)}
        title="Show Bob the Butler"
      >
        <img src="/assets/butler2.png" alt="Bob" className="bob-mini-avatar" />
      </button>
    );
  }

  return (
    <div className={`bob-widget ${isExpanded ? 'expanded' : ''}`}>
      {/* Top Bar */}
      <div className="bob-top-bar">
        <div className="bob-controls-left">
          <button 
            className="bob-btn-icon" 
            onClick={readLastMessage}
            title="Read last message"
          >
            🔊
          </button>
          <button 
            className="bob-btn-icon" 
            onClick={stopSpeech}
            title="Stop reading"
          >
            ⏹️
          </button>
        </div>
        
        <select 
          className="bob-voice-selector"
          value={availableVoices.indexOf(selectedVoice)}
          onChange={(e) => {
            const voice = availableVoices[parseInt(e.target.value)];
            if (voice) setSelectedVoice(voice);
          }}
        >
          {availableVoices.map((voice, index) => (
            <option key={index} value={index}>
              {voice.name} ({voice.lang})
            </option>
          ))}
        </select>

        <div className="bob-controls-right">
          <button 
            className="bob-btn-icon" 
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            ⤢
          </button>
          <button 
            className="bob-btn-icon" 
            onClick={() => setWidgetOpen(false)}
            title="Hide Bob"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="bob-body">
        {/* Left Side - Bob's Avatar */}
        <div className="bob-left">
          <div className="bob-portrait">
            <img 
              src="/assets/butler2.png" 
              alt="Bob the Butler" 
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
          <div className="bob-name">Bob the Butler</div>
        </div>

        {/* Right Side - Chat */}
        <div className="bob-right">
          <div className="bob-conversation" ref={conversationRef}>
            {conversation.map((msg) => (
              <div key={msg.id}>
                {msg.isOptions ? (
                  <div className="bob-options-grid">
                    {msg.options.map((option, index) => (
                      <button
                        key={index}
                        className="bob-option-btn"
                        onClick={() => selectOption(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={`bob-message ${msg.sender}-message ${msg.isThinking ? 'thinking' : ''}`}>
                    {msg.message}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Input Area */}
          <div className="bob-composer-wrap">
            <form onSubmit={handleSubmit} className="bob-composer">
              <button 
                type="button"
                className="bob-icon-btn" 
                onClick={showQuickOptions}
                title="Quick options"
              >
                ＋
              </button>
              
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Ask anything about English..."
                className="bob-input"
                rows="1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (inputMessage.trim() && !isLoading) {
                      handleSubmit(e);
                    }
                  }
                }}
                disabled={isLoading}
              />
              
              <button 
                type="button"
                className={`bob-icon-btn bob-mic ${isListening ? 'listening' : ''}`}
                onClick={toggleMicrophone}
                title={isListening ? "Stop listening" : "Start voice input"}
              >
                🎤
              </button>
              
              <button 
                type="submit"
                className="bob-send-btn"
                disabled={!inputMessage.trim() || isLoading}
                title="Send message"
              >
                ➤
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BobTheButler;